import React, { FormEvent, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Input, Button, Spinner } from "reactstrap";
import EmojiPicker from "emoji-picker-react";
import { RootState } from "../../../../../../ReduxToolkit/Store";
import { sendMessageAsync } from "../../../../../../ReduxToolkit/Reducers/ChatReducer";
import { getAPIURLAWS, getUserID } from "../../../../../../services/helper";
import "./../../../chat.css";
import http from "../../../../../../services/http";
import SendTCoin from "./SendTCoin";

export default function SendMessages() {
  const [messageInput, setMessageInput] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const {
    socket,
    isGroup,
    selectedGroup,
    connected,
    chats,
    selectedUser,
    currentUser,
  } = useSelector((state: RootState) => state.chat);
  const dispatch = useDispatch();
  const Tcoins = currentUser?.tcoins || 0;

  const chatHistoryRef = useRef<HTMLDivElement | null>(null);

  const addEmoji = (emoji: string) => {
    const text = `${messageInput}${emoji}`;
    setShowEmojiPicker(false);
    setMessageInput(text);
  };

  const scrollToBottom = () => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  };

  useEffect(scrollToBottom, [chats]);

  const handleMessageChange = (message: string) => {
    setMessageInput(message);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setSelectedFiles((prev) => [...prev, ...newFiles]);
      const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
      setFilePreviews((prev) => [...prev, ...newPreviews]);
    }
  };

  const uploadFiles = async (files: File[]): Promise<string[]> => {
    try {
      setUploading(true);
      const formData = new FormData();
      files.forEach((file) => formData.append("files[]", file));
      const api_url = getAPIURLAWS();

      const response = await http.post(`${api_url}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setUploading(false);

      if (
        response.data &&
        response.data.uploadedImages &&
        response.data.uploadedImages.length > 0
      ) {
        return response.data.uploadedImages;
      } else {
        console.error("File upload failed or no image URLs returned.");
        return [];
      }
    } catch (error) {
      setUploading(false);
      console.error("Error uploading files:", error);
      return [];
    }
  };

  const handleMessagePress = async () => {
    let uploadedFiles: string[] = [];

    if (selectedFiles.length > 0) {
      uploadedFiles = await uploadFiles(selectedFiles);

      if (uploadedFiles.length === 0) {
        console.error("File upload failed. Cannot send the message.");
        return;
      }
    }

    if (messageInput.trim() || uploadedFiles.length > 0) {
      if (socket && connected) {
        const payload: any = {
          action: "sendMessage",
          message: messageInput,
          sender: currentUser?.id,
          recipient: isGroup
            ? selectedUser.map((userId: string | number) =>
                parseInt(userId as string, 10)
              )
            : selectedUser?.id,
          groupId: isGroup ? selectedGroup.groupId : null,
          meta: uploadedFiles.length > 0 ? { files: uploadedFiles } : undefined,
        };

        socket.send(JSON.stringify(payload));
        setMessageInput("");
        setSelectedFiles([]);
        setFilePreviews([]);
        // scrollToBottom();
      } else {
        console.error("WebSocket is not connected.");
      }

      dispatch(
        sendMessageAsync({
          currentUserId: currentUser?.id,
          selectedUserId: selectedUser?.id,
          messageInput,
          chats,
        })
      );
    }
  };

  const handleShareTCoins = (tcoins: number) => {
    if (tcoins > 0) {
      const recipient = isGroup ? selectedUser : selectedUser?.id;

      if (socket && connected && recipient) {
        const payload = {
          action: "sendMessage",
          message: "",
          type: "tcoins",
          meta: { tcoins, shared_by: currentUser?.id, accepted: false },
          sender: currentUser?.id,
          recipient,
          groupId: isGroup ? selectedGroup.groupId : null,
        };

        socket.send(JSON.stringify(payload));

        // Scroll chat history to the bottom
        // scrollToBottom();
      } else {
        console.error("WebSocket is not connected.");
      }

      // Dispatch the sendMessageAsync action to update Redux store
      dispatch(
        sendMessageAsync({
          currentUserId: currentUser?.id,
          selectedUserId: selectedUser?.id,
          messageInput: "",
          type: "tcoins",
          meta: { tcoins, shared_by: currentUser?.id, accepted: false },
          chats,
        })
      );
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleMessagePress();
  };

  return (
    <Form className="msger-inputarea" onSubmit={(e) => handleSubmit(e)}>
      {/* <PlusIconDropdown /> */}
      {Tcoins > 0 && (
        <SendTCoin currentCoins={Tcoins} onShareTcoins={handleShareTCoins} />
      )}

      <div className="attachment-container">
        <label htmlFor="fileInput" className="file-attachment-icon">
          <i className="fa fa-paperclip" />
        </label>
        <input
          type="file"
          id="fileInput"
          accept="image/*,video/*"
          multiple
          className="d-none"
          onChange={handleFileChange}
        />
      </div>
      <Input
        type="text"
        className="msger-input uk-textarea"
        placeholder={!connected ? "Refresh page to chat" : "Type a message..."}
        value={messageInput}
        onChange={(e) => handleMessageChange(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleMessagePress();
          }
        }}
        disabled={!connected}
      />

      {/* File preview container positioned below the input */}
      {filePreviews.length > 0 && (
        <div className="file-preview-container">
          {filePreviews.map((preview, index) => {
            const file = selectedFiles[index];
            return file?.type.startsWith("image/") ? (
              <img
                key={index}
                src={preview}
                alt="File Preview"
                className="file-preview"
              />
            ) : (
              <video key={index} controls className="file-preview">
                <source src={preview} type={file.type} />
                Your browser does not support the video tag.
              </video>
            );
          })}
          <Button
            color="danger"
            size="sm"
            className="remove-preview"
            onClick={() => {
              setSelectedFiles([]);
              setFilePreviews([]);
            }}
          >
            Remove All
          </Button>
        </div>
      )}

      {uploading && <Spinner color="primary" className="uploading-spinner" />}

      <div>
        {showEmojiPicker ? (
          <EmojiPicker onEmojiClick={(e) => addEmoji(e.emoji)} />
        ) : null}
      </div>
      <div className="smiley-box">
        <div
          className="picker second-btn uk-button"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
        />
      </div>
      <Button
        type="button"
        color="primary"
        className="msger-send-btn"
        onClick={() => handleMessagePress()}
      >
        <i className="fa fa-location-arrow" />
      </Button>
    </Form>
  );
}
